import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  BookOpen,
  FileText,
  ChevronDown,
  ChevronUp,
  List,
  Info,
  Shield,
  Download,
  MessageSquare,
  Layers
} from 'lucide-react';

// ─────────────────────────────────────────────
// ReviewGate
// ─────────────────────────────────────────────
const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perché rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// Inferential phase config
// ─────────────────────────────────────────────
const PHASE_CONFIG = {
  attentional_orienting:           { label: 'Attentional Orienting',           text: 'text-pink-700',   bg: 'bg-pink-50',   border: 'border-l-pink-400',   hex: '#ec4899' },
  precision_establishment:         { label: 'Precision Establishment',         text: 'text-purple-700', bg: 'bg-purple-50', border: 'border-l-purple-400', hex: '#a855f7' },
  dominant_model_alignment:        { label: 'Dominant Model Alignment',        text: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-l-indigo-400', hex: '#6366f1' },
  observational_counter_evidence:  { label: 'Observational Counter-Evidence',  text: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-l-blue-400',   hex: '#3b82f6' },
  epistemic_instability:           { label: 'Epistemic Instability',           text: 'text-cyan-700',   bg: 'bg-cyan-50',   border: 'border-l-cyan-400',   hex: '#06b6d4' },
  spontaneous_inference_of_s_star: { label: 'Spontaneous Inference of S*',     text: 'text-teal-700',   bg: 'bg-teal-50',   border: 'border-l-teal-400',   hex: '#14b8a6' },
  policy_naturalization:           { label: 'Policy Naturalization',           text: 'text-green-700',  bg: 'bg-green-50',  border: 'border-l-green-400',  hex: '#22c55e' },
};

const IMPLEMENTATION_PROFILE_LABELS = {
  opening_strategy:    'Opening Strategy',
  observation_strategy:'Observation Strategy',
  scene_context:       'Scene Context',
  lexical_register:    'Lexical Register',
  dialogue_rhythm:     'Dialogue Rhythm',
  narrative_framing:   'Narrative Framing',
};

// ─────────────────────────────────────────────
// PDF export
// ─────────────────────────────────────────────
const exportScriptToPDF = (script, index) => {
  const esc = (str) => String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const segmentsHtml = (script.script_segments || []).map(seg => {
    const cfg = PHASE_CONFIG[seg.inferential_phase] || {};
    const color = cfg.hex || '#6b7280';
    return `
    <div class="segment" style="border-left-color:${color}">
      <p class="segment-label" style="color:${color}">${esc(cfg.label || seg.inferential_phase)}</p>
      <p class="segment-text">${esc(seg.text)}</p>
    </div>`;
  }).join('');

  const profileHtml = script.implementation_profile
    ? Object.entries(IMPLEMENTATION_PROFILE_LABELS).map(([key, label]) => {
        const val = script.implementation_profile[key];
        if (!val) return '';
        return `<div class="profile-row"><span class="profile-key">${esc(label)}</span><span class="profile-val">${esc(val)}</span></div>`;
      }).join('')
    : '';

  const html = `<!DOCTYPE html><html lang="it"><head>
<meta charset="UTF-8">
<title>${esc(script.script_id || `Script ${index + 1}`)}</title>
<style>
  *, *::before, *::after { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11pt; line-height: 1.65; color: #1a1a2e; margin: 0; padding: 0; background: #fff; }
  .header { background: linear-gradient(135deg, #be185d 0%, #7c3aed 100%); color: #fff; padding: 26pt 30pt 22pt; }
  .header-tag { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .14em; opacity: .75; margin-bottom: 8pt; }
  .header h1 { font-size: 21pt; font-weight: 800; margin: 0 0 5pt; letter-spacing: -.02em; }
  .header .duration { font-size: 10pt; opacity: .8; margin: 0; }
  .spoken-block { background: #fdf2f8; border-bottom: 2pt solid #fbcfe8; padding: 14pt 30pt; }
  .spoken-label { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .1em; color: #9d174d; margin-bottom: 6pt; }
  .spoken-text { font-size: 11.5pt; line-height: 1.8; color: #1f2937; white-space: pre-wrap; }
  .section-title { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .12em; color: #6b7280; padding: 12pt 30pt 6pt; border-top: 1pt solid #e5e7eb; margin-top: 6pt; }
  .segments { padding: 0 30pt 20pt; }
  .segment { margin-bottom: 12pt; padding-left: 12pt; border-left: 3pt solid #fbcfe8; }
  .segment-label { font-size: 7pt; font-weight: 800; text-transform: uppercase; letter-spacing: .1em; margin: 0 0 3pt; }
  .segment-text { font-size: 10.5pt; color: #1f2937; line-height: 1.7; white-space: pre-wrap; margin: 0; }
  .profile { padding: 0 30pt 20pt; }
  .profile-row { display: flex; gap: 10pt; margin-bottom: 6pt; }
  .profile-key { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #6b7280; width: 120pt; flex-shrink: 0; padding-top: 1pt; }
  .profile-val { font-size: 10pt; color: #374151; }
  .footer { margin: 0 30pt; padding: 10pt 0; border-top: 1pt solid #e5e7eb; display: flex; justify-content: space-between; font-size: 7.5pt; color: #9ca3af; }
  .footer-dot { display: inline-block; width: 5pt; height: 5pt; border-radius: 50%; background: #be185d; margin-right: 5pt; vertical-align: middle; }
  @page { margin: 0; size: A4; }
</style>
</head><body>
<div class="header">
  <div class="header-tag">Script Library Production — C11</div>
  <h1>${esc(script.script_id || `Script ${index + 1}`)}</h1>
  ${script.estimated_duration_seconds ? `<p class="duration">Durata stimata: ${script.estimated_duration_seconds}s</p>` : ''}
</div>
${script.spoken_text ? `
<div class="spoken-block">
  <p class="spoken-label">Testo Completo</p>
  <p class="spoken-text">${esc(script.spoken_text)}</p>
</div>` : ''}
${segmentsHtml ? `<p class="section-title">Segmenti per Fase Inferenziale</p><div class="segments">${segmentsHtml}</div>` : ''}
${profileHtml ? `<p class="section-title">Implementation Profile</p><div class="profile">${profileHtml}</div>` : ''}
<div class="footer">
  <span><span class="footer-dot"></span>${esc(script.script_id || `Script ${index + 1}`)}</span>
  <span>Adraise — Script Library</span>
</div>
</body></html>`;

  const w = window.open('', '_blank', 'width=860,height=760');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 500);
};

// ─────────────────────────────────────────────
// ScriptCard — single script from script_library
// ─────────────────────────────────────────────
const ScriptCard = ({ script, index }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const [showPhaseUsage, setShowPhaseUsage] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const profile = script.implementation_profile || {};
  const phaseUsage = script.inferential_phase_usage || {};
  const segments = script.script_segments || [];
  const consumerObs = script.consumer_observation_usage || [];

  return (
    <div className="rounded-lg border border-pink-200 overflow-hidden">
      {/* Header row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-pink-50 hover:opacity-90 transition-opacity">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-200 text-pink-800 text-xs font-bold flex-shrink-0">
            {index + 1}
          </div>
          <div className="text-left min-w-0">
            <p className="text-sm font-semibold text-pink-800">
              {script.script_id || `Script ${index + 1}`}
            </p>
            {profile.opening_strategy && (
              <p className="text-xs text-pink-500 mt-0.5 truncate">{profile.opening_strategy}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {script.estimated_duration_seconds && (
            <span className="text-xs text-pink-400 bg-white border border-pink-200 px-2 py-0.5 rounded">
              {script.estimated_duration_seconds}s
            </span>
          )}
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); exportScriptToPDF(script, index); }}
            className="flex items-center gap-1 px-2 py-1 rounded bg-white border border-pink-200 text-pink-600 hover:bg-pink-100 hover:border-pink-400 text-xs font-medium transition-colors"
            title="Esporta come PDF"
          >
            <Download className="w-3 h-3" />
            PDF
          </span>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-pink-500" />
            : <ChevronDown className="w-4 h-4 text-pink-500" />}
        </div>
      </button>

      {expanded && (
        <div className="bg-white divide-y divide-gray-100">

          {/* Spoken text */}
          {script.spoken_text && (
            <div className="p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <MessageSquare className="w-3.5 h-3.5 text-pink-500" />
                <p className="text-xs font-semibold uppercase tracking-wide text-pink-600">Testo Completo</p>
              </div>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap bg-pink-50 rounded p-3 border border-pink-100">
                {script.spoken_text}
              </p>
            </div>
          )}

          {/* Script segments by inferential phase */}
          {segments.length > 0 && (
            <div className="p-4">
              <div className="flex items-center gap-1.5 mb-3">
                <Layers className="w-3.5 h-3.5 text-indigo-500" />
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Segmenti per Fase Inferenziale</p>
              </div>
              <div className="space-y-2">
                {segments.map((seg, si) => {
                  const cfg = PHASE_CONFIG[seg.inferential_phase] || {};
                  return (
                    <div key={si} className={`pl-3 border-l-4 ${cfg.border || 'border-l-gray-300'}`}>
                      <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${cfg.text || 'text-gray-600'}`}>
                        {cfg.label || seg.inferential_phase}
                      </p>
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {seg.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Consumer observations + verifiable policy element */}
          {(consumerObs.length > 0 || script.verifiable_policy_element) && (
            <div className="p-4 space-y-2">
              {consumerObs.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Consumer Observations Used</p>
                  <div className="flex flex-wrap gap-1.5">
                    {consumerObs.map((obs, oi) => (
                      <span key={oi} className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">{obs}</span>
                    ))}
                  </div>
                </div>
              )}
              {script.verifiable_policy_element && (
                <div className="flex items-start gap-2">
                  <Shield className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0.5">Verifiable Policy Element</p>
                    <p className="text-xs text-green-700">{script.verifiable_policy_element}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inferential phase usage — collapsible */}
          {Object.keys(phaseUsage).length > 0 && (
            <div>
              <button
                onClick={() => setShowPhaseUsage(!showPhaseUsage)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Inferential Phase Usage</p>
                {showPhaseUsage ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
              </button>
              {showPhaseUsage && (
                <div className="px-4 pb-4 pt-2 space-y-2 bg-white">
                  {Object.entries(PHASE_CONFIG).map(([key, cfg]) => {
                    const val = phaseUsage[key];
                    if (!val) return null;
                    return (
                      <div key={key}>
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${cfg.text}`}>{cfg.label}</p>
                        <p className="text-xs text-gray-700 leading-relaxed">{val}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Implementation profile — collapsible */}
          {Object.keys(profile).length > 0 && (
            <div>
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Implementation Profile</p>
                {showProfile ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
              </button>
              {showProfile && (
                <div className="px-4 pb-4 pt-2 bg-white">
                  <dl className="space-y-1.5">
                    {Object.entries(IMPLEMENTATION_PROFILE_LABELS).map(([key, label]) => {
                      const val = profile[key];
                      if (!val) return null;
                      return (
                        <div key={key} className="flex gap-3">
                          <dt className="text-xs font-medium text-gray-500 w-36 flex-shrink-0 pt-px">{label}</dt>
                          <dd className="text-xs text-gray-800 leading-relaxed">{val}</dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// VariantCard — single entry from script_variants
// ─────────────────────────────────────────────
const VariantCard = ({ variant }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
    <div className="flex items-center gap-2">
      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded font-medium">{variant.script_id}</span>
      {variant.variant_type && (
        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">{variant.variant_type}</span>
      )}
    </div>
    {variant.variant_description && (
      <p className="text-sm text-gray-700">{variant.variant_description}</p>
    )}
    {variant.invariant_check && (
      <div className="flex items-start gap-2 pt-1">
        <Shield className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-green-700">{variant.invariant_check}</p>
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────
// ProductionMetaCard
// ─────────────────────────────────────────────
const ProductionMetaCard = ({ meta = {} }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:opacity-90 transition-opacity">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-gray-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Production Metadata</span>
          {meta.total_scripts !== undefined && (
            <span className="ml-2 text-xs text-gray-400">{meta.total_scripts} script{meta.total_scripts !== 1 ? 's' : ''}</span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="bg-white p-4 space-y-3 border-t border-gray-100">
          {meta.implementation_invariance_confirmation && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Implementation Invariance</p>
              <p className="text-sm text-gray-700">{meta.implementation_invariance_confirmation}</p>
            </div>
          )}
          {meta.script_library_constraints_compliance && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Script Library Constraints</p>
              <p className="text-sm text-green-700">{meta.script_library_constraints_compliance}</p>
            </div>
          )}
          {meta.forbidden_modifications_check && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Forbidden Modifications Check</p>
              <p className="text-sm text-green-700">{meta.forbidden_modifications_check}</p>
            </div>
          )}
          {Array.isArray(meta.variation_dimensions_used) && meta.variation_dimensions_used.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Variation Dimensions Used</p>
              <div className="flex flex-wrap gap-1.5">
                {meta.variation_dimensions_used.map((d, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center text-gray-500">
      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
      <p className="text-sm">Clicca "Esegui Contract" per avviare la Script Library Production</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Contract11View
// ─────────────────────────────────────────────
const Contract11View = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};
  const {
    script_library = [],
    script_variants = [],
    production_metadata = {}
  } = output;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BookOpen className="w-6 h-6 text-pink-600" />
            <h2 className="text-xl font-bold text-gray-800">Script Library Production</h2>
            <span className="px-2 py-0.5 text-xs bg-pink-100 text-pink-700 rounded-full font-medium">C11</span>
            {script_library.length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                {script_library.length} script{script_library.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">
            Libreria di script narrativi completi — implementazioni differenti della medesima architettura inferenziale
          </p>
        </div>

        {/* Production Metadata */}
        <ProductionMetaCard meta={production_metadata} />

        {/* Script Library */}
        {script_library.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-pink-600" />
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Script Library</h3>
            </div>
            <div className="space-y-3">
              {script_library.map((script, i) => (
                <ScriptCard key={script.script_id || i} script={script} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* Script Variants */}
        {script_variants.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <List className="w-4 h-4 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Script Variants</h3>
              <span className="text-xs text-gray-400">{script_variants.length} entries</span>
            </div>
            <div className="space-y-2">
              {script_variants.map((variant, i) => (
                <VariantCard key={variant.script_id || i} variant={variant} />
              ))}
            </div>
          </div>
        )}

        {/* Review Gate */}
        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />

      </div>
    </div>
  );
};

export default Contract11View;
